import type { ReactNode } from "react";
import { useEffect, useId, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Badge } from "../primitives/Badge";
import { Button } from "../primitives/Button";
import { Dropdown } from "../primitives/Dropdown";
import { Input } from "../primitives/Input";
import { Select } from "../primitives/Select";
import { Table } from "../primitives/Table";
import { Checkbox } from "../primitives/Checkbox";

type OperationItem = {
  id: string;
  title: string;
  subtitle?: string;
  status: { label: string; tone: "neutral" | "success" | "warning" | "danger" };
  amount: string;
  date: string;
  meta?: string;
};

type ColumnType = "title" | "status" | "amount" | "date" | "actions";

type Column = {
  key: ColumnType;
  label: string;
  width?: number;
  sortable?: boolean;
  hideable?: boolean;
};

type Action = { label: string; onClick?: () => void; disabled?: boolean };

export type OperationBoardPatternProps = {
  title?: string;
  items: OperationItem[];
  rowActions?: Action[];
  searchPlaceholder?: string;
  statusOptions?: string[];
  actions?: ReactNode;
  enableColumnVisibility?: boolean;
  sortState?: { key: ColumnType; direction: "asc" | "desc" };
  onSortChange?: (next: { key: ColumnType; direction: "asc" | "desc" }) => void;
  stickyHeader?: boolean;
  minTableHeight?: number;
  pagination?: ReactNode;
  isLoading?: boolean;
  emptyState?: { title: string; description?: string };
  errorState?: { title: string; description?: string };
};

const defaultColumns: Column[] = [
  { key: "title", label: "Opération", sortable: true },
  { key: "status", label: "Statut" },
  { key: "amount", label: "Montant", width: 140, sortable: true, hideable: false },
  { key: "date", label: "Date", width: 120, sortable: true },
  { key: "actions", label: "", width: 56, hideable: false },
];

export function OperationBoardPattern({
  title = "Opérations",
  items,
  rowActions,
  searchPlaceholder = "Rechercher...",
  statusOptions = ["Tous", "Actif", "En attente", "En retard"],
  actions,
  enableColumnVisibility = true,
  sortState,
  onSortChange,
  stickyHeader = false,
  minTableHeight = 420,
  pagination,
  isLoading = false,
  emptyState,
  errorState,
}: OperationBoardPatternProps) {
  const fallbackPagination =
    pagination ?? (items.length ? <div className="text-xs text-muted">1–{Math.min(10, items.length)} sur {items.length}</div> : null);
  const [internalSort, setInternalSort] = useState<{ key: ColumnType; direction: "asc" | "desc" } | null>(null);
  const [visibleKeys, setVisibleKeys] = useState<ColumnType[]>([]);
  const currentSort = sortState ?? internalSort;

  useEffect(() => {
    const initialVisible = defaultColumns.filter((column) => column.hideable !== false).map((column) => column.key);
    setVisibleKeys((prev) => (prev.length ? prev : initialVisible));
  }, []);

  const columns = defaultColumns.filter((column) =>
    column.hideable === false ? true : visibleKeys.includes(column.key)
  );
  const columnAligns = columns.map((column) => (column.key === "title" || column.key === "status" ? "left" : "right"));
  const columnWidths = columns.map((column) => {
    if (column.width) return `${column.width}px`;
    return column.key === "title" ? "minmax(0, 1fr)" : "120px";
  });

  const sortedItems = getSortedItems(items, currentSort);
  const rows = sortedItems.map((item) => {
    const actionCell = rowActions ? (
      <div className="flex justify-end">
        <Dropdown
          label="⋯"
          items={rowActions.map((action) => ({
            label: action.label,
            disabled: action.disabled,
            onClick: action.onClick,
          }))}
          align="right"
        />
      </div>
    ) : null;
    const statusBarClass =
      item.status.tone === "success"
        ? "bg-success"
        : item.status.tone === "warning"
        ? "bg-warning"
        : item.status.tone === "danger"
        ? "bg-muted"
        : "bg-muted";
    return columns.map((column) => {
      if (column.key === "actions") return actionCell;
      if (column.key === "status") return <Badge label={item.status.label} tone={item.status.tone} />;
      if (column.key === "amount") return <span className="tabular-nums font-semibold">{item.amount}</span>;
      if (column.key === "date") return <span className="tabular-nums">{item.date}</span>;
      return (
        <div className="flex gap-3">
          <span className={`mt-1 h-10 w-0.5 rounded-full ${statusBarClass}`} />
          <div>
            <div className="text-sm font-medium text-text">{item.title}</div>
          {item.subtitle ? <div className="text-xs text-muted">{item.subtitle}</div> : null}
          {item.meta ? <div className="text-xs text-muted">{item.meta}</div> : null}
          </div>
        </div>
      );
    });
  });

  const headerCells = columns.map((column) => {
    if (column.key === "actions") return "";
    const isSortable = column.sortable;
    const isActive = currentSort?.key === column.key;
    const icon = isSortable ? (isActive ? (currentSort?.direction === "asc" ? "▲" : "▼") : "↕") : null;
    const justify = column.key === "title" || column.key === "status" ? "justify-start" : "justify-end";
    if (!isSortable) {
      return <div className={`flex w-full items-center ${justify}`}>{column.label}</div>;
    }
    return (
      <button
        type="button"
        className={`flex w-full items-center gap-1 ${justify}`}
        onClick={() => {
          const next =
            currentSort?.key === column.key && currentSort.direction === "asc"
              ? { key: column.key, direction: "desc" as const }
              : { key: column.key, direction: "asc" as const };
          onSortChange?.(next);
          if (!onSortChange) setInternalSort(next);
        }}
      >
        <span>{column.label}</span>
        {icon ? <span className={isActive ? "text-text" : "text-muted"}>{icon}</span> : null}
      </button>
    );
  });

  return (
    <div className="rounded-md border border-border bg-surface p-6 shadow-sm">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="text-sm font-semibold text-text">{title}</div>
        {actions ? <div className="flex items-center gap-2">{actions}</div> : null}
      </div>
      <div className="mt-3 flex flex-wrap items-center justify-between gap-3">
        <div className="min-w-[220px] flex-1">
          <Input label="Recherche" placeholder={searchPlaceholder} hideLabel ariaLabel="Recherche" />
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Select label="Statut" options={statusOptions} hideLabel ariaLabel="Statut" />
          {enableColumnVisibility ? (
            <ColumnVisibilityMenu
              columns={defaultColumns.filter((column) => column.key !== "actions")}
              visibleKeys={visibleKeys}
              onToggle={(key) =>
                setVisibleKeys((prev) => (prev.includes(key) ? prev.filter((item) => item !== key) : [...prev, key]))
              }
            />
          ) : null}
        </div>
      </div>

      <div className="mt-4" style={{ minHeight: `${minTableHeight}px` }}>
        {isLoading ? (
          <div className="space-y-3">
            {Array.from({ length: 4 }).map((_, index) => (
              <div key={index} className="h-10 rounded-md border border-border bg-surface-muted" />
            ))}
          </div>
        ) : null}

        {!isLoading && errorState ? (
          <div className="rounded-md border border-border bg-surface p-6 text-sm">
            <div className="font-semibold text-danger">{errorState.title}</div>
            {errorState.description ? <div className="mt-2 text-muted">{errorState.description}</div> : null}
          </div>
        ) : null}

        {!isLoading && !errorState && !items.length ? (
          <div className="rounded-md border border-border bg-surface p-6 text-sm">
            <div className="font-semibold text-text">{emptyState?.title ?? "Aucune opération."}</div>
            {emptyState?.description ? <div className="mt-2 text-muted">{emptyState.description}</div> : null}
          </div>
        ) : null}

        {!isLoading && !errorState && items.length ? (
          <div className="overflow-auto">
            <Table
              columns={headerCells}
              rows={rows}
              columnAligns={columnAligns}
              gridTemplateColumns={columnWidths.join(" ")}
              headerClassName={stickyHeader ? "sticky top-0 z-10" : undefined}
            />
          </div>
        ) : null}
      </div>
      {fallbackPagination ? <div className="mt-4 border-t border-border pt-3">{fallbackPagination}</div> : null}
    </div>
  );
}

function getSortedItems(
  items: OperationItem[],
  sortState: { key: ColumnType; direction: "asc" | "desc" } | null
) {
  if (!sortState) return items;
  const direction = sortState.direction === "asc" ? 1 : -1;
  const getValue = (item: OperationItem) => {
    if (sortState.key === "amount") {
      const parsed = Number(item.amount.replace(/[^0-9.-]+/g, ""));
      return Number.isNaN(parsed) ? 0 : parsed;
    }
    if (sortState.key === "date") return item.date;
    if (sortState.key === "title") return item.title.toLowerCase();
    return "";
  };
  return [...items].sort((a, b) => {
    const aValue = getValue(a);
    const bValue = getValue(b);
    if (aValue < bValue) return -1 * direction;
    if (aValue > bValue) return 1 * direction;
    return 0;
  });
}

function ColumnVisibilityMenu({
  columns,
  visibleKeys,
  onToggle,
}: {
  columns: Column[];
  visibleKeys: ColumnType[];
  onToggle: (key: ColumnType) => void;
}) {
  const menuId = useId();
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);
  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState<{ top: number; left: number }>({ top: 0, left: 0 });

  useEffect(() => {
    const handleGlobalOpen = (event: Event) => {
      const customEvent = event as CustomEvent<string>;
      if (customEvent.detail !== menuId) {
        setOpen(false);
      }
    };
    document.addEventListener("xlflow-operation-columns-open", handleGlobalOpen as EventListener);
    return () => {
      document.removeEventListener("xlflow-operation-columns-open", handleGlobalOpen as EventListener);
    };
  }, [menuId]);

  useEffect(() => {
    if (!open) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };
    const onMouseDown = (event: MouseEvent) => {
      const target = event.target as Node;
      if (menuRef.current?.contains(target) || buttonRef.current?.contains(target)) {
        return;
      }
      setOpen(false);
    };
    document.addEventListener("keydown", onKeyDown);
    document.addEventListener("mousedown", onMouseDown);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.removeEventListener("mousedown", onMouseDown);
    };
  }, [open]);

  useLayoutEffect(() => {
    if (!open) return;
    const trigger = buttonRef.current;
    const menu = menuRef.current;
    if (!trigger || !menu) return;
    const rect = trigger.getBoundingClientRect();
    const menuRect = menu.getBoundingClientRect();
    const left = Math.min(rect.left, window.innerWidth - menuRect.width - 8);
    const top = rect.bottom + 6;
    setPosition({ top, left: Math.max(8, left) });
  }, [open]);

  return (
    <div className="relative">
      <button
        ref={buttonRef}
        type="button"
        className="rounded-md border border-border bg-surface px-3 py-1 text-sm text-text"
        onClick={() => {
          const next = !open;
          if (next) document.dispatchEvent(new CustomEvent("xlflow-operation-columns-open", { detail: menuId }));
          setOpen(next);
        }}
      >
        Colonnes
      </button>
      {open
        ? createPortal(
            <div
              ref={menuRef}
              className="fixed z-50 w-56 rounded-md border border-border bg-surface p-3 shadow-sm"
              style={{ top: position.top, left: position.left }}
            >
              <div className="text-xs uppercase tracking-[0.04em] text-muted font-medium">Colonnes</div>
              <div className="mt-2 grid gap-2">
                {columns.map((column) => {
                  const hideable = column.hideable !== false;
                  const isChecked = visibleKeys.includes(column.key);
                  return (
                    <div key={column.key} className="flex items-center justify-between">
                      <Checkbox
                        label={column.label}
                        checked={isChecked}
                        state={hideable ? "default" : "disabled"}
                        onChange={() => {
                          if (!hideable) return;
                          onToggle(column.key);
                        }}
                      />
                      {hideable ? (
                        <button
                          type="button"
                          className="text-xs text-muted"
                          onClick={() => onToggle(column.key)}
                        >
                          {isChecked ? "Masquer" : "Afficher"}
                        </button>
                      ) : null}
                    </div>
                  );
                })}
              </div>
            </div>,
            document.body
          )
        : null}
    </div>
  );
}
